import {ClientEvent, Tracker} from '@udemy/event-tracking';

import {
    PopQuizAlternateQuizSelected,
    PopQuizContinueCourseSelected,
    PopQuizDismissed,
    PopQuizFeedbackSubmitted,
    PopQuizPresented,
    PopQuizRecommendedAssessmentLinkSelected,
    PopQuizRecommendedAssessmentPresented,
    PopQuizRelatedLectureLinkPresented,
    PopQuizRelatedLectureLinkSelected,
    PopQuizSnoozed,
    QuizCompleted,
    QuizStarted,
    RecommendedAssessmentEventPayload,
} from './events';
import {PopQuizFeedbackRating, PopQuizLectureType, QuizData} from './types';

/**
 * True when a debug-forced Pop Quiz session's side effects (tracking events, attempt mutations)
 * should be suppressed. Suppresses both in prod due to data pollution concerns.
 *
 * Exported publicly — beyond gating events here, `usePopQuizLifecycle()`'s `onAccept` also
 * uses it to decide whether to call the `startAttempt` mutation at all for a debug quiz.
 */
export function isDebugQuizSuppressed(quizData: QuizData): boolean {
    return Boolean(quizData.isDebugQuiz) && process.env.DEPLOY_ENV === 'prod';
}

function publish(event: ClientEvent, quizData: QuizData): void {
    if (!isDebugQuizSuppressed(quizData)) {
        Tracker.publishEvent(event);
    }
}

export function publishPresented(quizData: QuizData, numberOfQuestions: number): void {
    publish(
        new PopQuizPresented({id: quizData.quizId, numberOfQuestions}, {id: quizData.courseId}),
        quizData,
    );
}

export function publishRecommendedAssessmentPresented(
    quizData: QuizData,
    numberOfQuestions: number,
    assessment: RecommendedAssessmentEventPayload,
): void {
    publish(
        new PopQuizRecommendedAssessmentPresented(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            assessment,
        ),
        quizData,
    );
}

/**
 * `quizData` is optional (unlike every other `publish*` function here) and a no-op when absent —
 * this lets `onPromptDismiss`'s "dismissed without snoozing" branch call this unconditionally
 * rather than wrapping it in its own `if (quizData)` check.
 */
export function publishDismissed(quizData: QuizData | undefined, numberOfQuestions: number): void {
    if (!quizData) {
        return;
    }
    publish(
        new PopQuizDismissed({id: quizData.quizId, numberOfQuestions}, {id: quizData.courseId}),
        quizData,
    );
}

export function publishSnoozed(
    quizData: QuizData,
    numberOfQuestions: number,
    snoozeDurationDays: number,
): void {
    publish(
        new PopQuizSnoozed(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            snoozeDurationDays,
        ),
        quizData,
    );
}

export function publishAlternateQuizSelected(quizData: QuizData, numberOfQuestions: number): void {
    publish(
        new PopQuizAlternateQuizSelected(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
        ),
        quizData,
    );
}

export function publishQuizStarted(
    quizData: QuizData,
    numberOfQuestions: number,
    attemptId: number,
): void {
    publish(
        new QuizStarted(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            attemptId,
        ),
        quizData,
    );
}

export function publishFeedbackSubmitted(
    quizData: QuizData,
    numberOfQuestions: number,
    rating: PopQuizFeedbackRating,
): void {
    publish(
        new PopQuizFeedbackSubmitted(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            rating,
        ),
        quizData,
    );
}

export function publishRecommendedAssessmentLinkSelected(
    quizData: QuizData,
    numberOfQuestions: number,
    assessment: RecommendedAssessmentEventPayload,
): void {
    publish(
        new PopQuizRecommendedAssessmentLinkSelected(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            assessment,
        ),
        quizData,
    );
}

export function publishContinueCourseSelected(quizData: QuizData, numberOfQuestions: number): void {
    publish(
        new PopQuizContinueCourseSelected(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
        ),
        quizData,
    );
}

export function publishQuizCompleted(
    quizData: QuizData,
    numberOfQuestions: number,
    attemptId: number,
    percentCorrect: number,
): void {
    publish(
        new QuizCompleted(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            attemptId,
            percentCorrect,
        ),
        quizData,
    );
}

export function publishRelatedLectureLinkPresented(
    quizData: QuizData,
    numberOfQuestions: number,
    lecture: {id: number; type: PopQuizLectureType},
): void {
    publish(
        new PopQuizRelatedLectureLinkPresented(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            lecture,
        ),
        quizData,
    );
}

export function publishRelatedLectureLinkSelected(
    quizData: QuizData,
    numberOfQuestions: number,
    lecture: {id: number; type: PopQuizLectureType},
): void {
    publish(
        new PopQuizRelatedLectureLinkSelected(
            {id: quizData.quizId, numberOfQuestions},
            {id: quizData.courseId},
            lecture,
        ),
        quizData,
    );
}
